/**
 * DrManiIcons - single-weight plant and nature line icons for the DMM quiz.
 *
 * Answer options no longer use these: they render emoji from
 * data/drManiQuiz/iconEmoji.ts, matching the USCN quiz. The ICONS map and the
 * DrManiIcon export below are kept only until that change is signed off.
 *
 * Still in active use: DmmSproutMark (reading counter), DmmSeedStage (reveal
 * build-up), and DmmDriftLeaf (celebration). All are 24x24, stroke
 * currentColor, so they inherit their container's text color from CSS.
 */
import React from 'react';

const STROKE = 1.6;

interface SvgProps {
  className?: string | undefined;
  size?: number | undefined;
}

/** Shared wrapper so every icon keeps the exact same single-weight style. */
const Svg: React.FC<SvgProps & { children: React.ReactNode; label?: string }> = ({
  className,
  size = 26,
  children,
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={STROKE}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

/* Small reusable leaf lens shape, drawn from two mirrored arcs. */
const leafPath = (x: number, y: number, s: number, flip = false): string => {
  const d = flip ? -s : s;
  return `M${x} ${y}c${-d * 0.9} ${-s * 0.15} ${-d * 1.45} ${-s * 0.75} ${-d * 1.6} ${-s * 1.6}c${d * 0.9} ${s * 0.15} ${d * 1.45} ${s * 0.75} ${d * 1.6} ${s * 1.6}z`;
};

const ICONS: Record<string, React.ReactNode> = {
  // ===== Q1: Where do you grow =====
  building_balcony: (
    <>
      <path d="M4 21h16" />
      <path d="M6 21V4h8v17" />
      <path d="M8.6 7.5h1.6M8.6 11h1.6" />
      <path d="M14 15h5.5" />
      <path d="M16 15v-1.8h2.2V15" />
      <path d="M17.1 13.2v-1.4" />
      <path d={leafPath(17.1, 11.8, 1.6)} />
    </>
  ),
  house_grass: (
    <>
      <path d="M3 21h18" />
      <path d="M4.5 21v-8.5L10 8l5.5 4.5V21" />
      <path d="M8.6 21v-3.6h2.8V21" />
      <path d="M17.6 21v-2.6M19.3 21v-3.8M21 21v-2.6" />
    </>
  ),
  house_potted: (
    <>
      <path d="M3 21h18" />
      <path d="M4.5 21v-8.5L10 8l5.5 4.5V21" />
      <path d="M17.4 18h3.8l-.6 3h-2.6z" />
      <path d="M19.3 18v-1.8" />
      <path d={leafPath(19.3, 16.2, 1.7)} />
    </>
  ),
  barn_fence: (
    <>
      <path d="M2.5 21h19" />
      <path d="M3.5 21v-7.5L8 10l4.5 3.5V21" />
      <path d="M6.4 21v-3.4h3.2V21" />
      <path d="M15.5 21v-4.5M18.5 21v-4.5M21.5 21v-4.5" />
      <path d="M14.5 18h8" />
    </>
  ),

  // ===== Q2: What do you grow =====
  tomato_vine: (
    <>
      <circle cx="12" cy="14" r="5.6" />
      <path d="M12 8.4V5.6" />
      <path d="M12 5.6c-1 .8-2.2 1-3.4.8" />
      <path d="M12 8.4l-2.4-1.2M12 8.4l2.4-1.2" />
    </>
  ),
  herb_sprig: (
    <>
      <path d="M12 21V4.5" />
      <path d={leafPath(12, 18, 2.1)} />
      <path d={leafPath(12, 15, 2.1, true)} />
      <path d={leafPath(12, 12, 2.1)} />
      <path d={leafPath(12, 9, 2.1, true)} />
      <path d={leafPath(12.8, 5.4, 1.9)} />
    </>
  ),
  open_bloom: (
    <>
      <circle cx="12" cy="12" r="2.4" />
      <path d="M12 9.6V4.5M12 14.4v5.1M14.4 12h5.1M9.6 12H4.5" />
      <path d="M13.7 10.3l3.6-3.6M10.3 13.7l-3.6 3.6M13.7 13.7l3.6 3.6M10.3 10.3L6.7 6.7" />
    </>
  ),
  plant_pot: (
    <>
      <path d="M7.5 13.5h9" />
      <path d="M8.4 13.5l1 7h5.2l1-7" />
      <path d="M12 13.5V9" />
      <path d={leafPath(12, 9.4, 2.3)} />
      <path d={leafPath(12, 7.6, 2.3, true)} />
    </>
  ),
  fruit_tree: (
    <>
      <circle cx="12" cy="9" r="5.8" />
      <path d="M12 14.8V21" />
      <path d="M8.5 21h7" />
      <circle cx="9.8" cy="8.2" r="1.1" />
      <circle cx="14.1" cy="10.4" r="1.1" />
    </>
  ),
  grape_cluster: (
    <>
      <path d="M12 8.8V5" />
      <path d={leafPath(14.6, 5.4, 1.9)} />
      <circle cx="8.9" cy="10.8" r="1.9" />
      <circle cx="12" cy="10.8" r="1.9" />
      <circle cx="15.1" cy="10.8" r="1.9" />
      <circle cx="10.4" cy="14.2" r="1.9" />
      <circle cx="13.6" cy="14.2" r="1.9" />
      <circle cx="12" cy="17.6" r="1.9" />
    </>
  ),
  simple_tree: (
    <>
      <circle cx="12" cy="8.6" r="5.6" />
      <path d="M12 14.2V21" />
      <path d="M8.5 21h7" />
    </>
  ),
  cactus_pot: (
    <>
      <path d="M10.8 16V8a1.2 1.2 0 0 1 2.4 0v8" />
      <path d="M10.8 12.6H9.2a1.2 1.2 0 0 1-1.2-1.2V9.2" />
      <path d="M13.2 10.6h1.6A1.2 1.2 0 0 0 16 9.4V7.4" />
      <path d="M8 16h8" />
      <path d="M8.8 16l.8 5h4.8l.8-5" />
    </>
  ),
  butterfly_flower: (
    <>
      <path d="M12 4.6v3.8" />
      <path d="M11 4.8l-1-1.2M13 4.8l1-1.2" />
      <path d="M12 6.5c-1.4-2-4.1-1.6-3.6.6.4 1.7 2.3 1.4 3.6-.6z" />
      <path d="M12 6.5c1.4-2 4.1-1.6 3.6.6-.4 1.7-2.3 1.4-3.6-.6z" />
      <circle cx="12" cy="16.4" r="1.9" />
      <path d="M12 14.5v-2.3M12 18.3v2.3M13.9 16.4h2.3M10.1 16.4H7.8" />
      <path d="M13.4 15l1.7-1.7M10.6 17.8l-1.7 1.7M13.4 17.8l1.7 1.7M10.6 15l-1.7-1.7" />
    </>
  ),
  seedling: (
    <>
      <path d="M8 21h8" />
      <path d="M12 21v-7.5" />
      <path d={leafPath(12, 13.5, 2.6)} />
      <path d={leafPath(12, 13.5, 2.6, true)} />
    </>
  ),
  grass_blades: (
    <>
      <path d="M6.5 21h11" />
      <path d="M9 21c-.8-2.6-.6-5.2.8-7.6" />
      <path d="M12 21v-8.6" />
      <path d="M15 21c.8-2.6.6-5.2-.8-7.6" />
    </>
  ),

  // ===== Q3: How do you garden =====
  trowel_soil: (
    <>
      <path d="M12 3.5v3" />
      <path d="M9.8 6.5h4.4l-1 6.2c-.2 1.4-2.2 1.4-2.4 0z" />
      <path d="M4.5 19.2c1.6-1.1 3.2-1.1 4.9 0s3.4 1.1 5.1 0 3.4-1.1 5 0" />
    </>
  ),
  raised_bed: (
    <>
      <path d="M4 13.4h16v4.8H4z" />
      <path d="M5.6 21v-2.8M18.4 21v-2.8" />
      <path d="M12 13.4v-3" />
      <path d={leafPath(12, 10.4, 2.1)} />
      <path d={leafPath(12, 10.4, 2.1, true)} />
    </>
  ),
  two_pots: (
    <>
      <path d="M5 10.5h7" />
      <path d="M5.9 10.5l1 9.5h3.2l1-9.5" />
      <path d="M14 15h7" />
      <path d="M14.8 15l.8 5h2.8l.8-5" />
    </>
  ),
  windowsill_pot: (
    <>
      <path d="M5.5 16V3.5h13V16" />
      <path d="M12 3.5v6" />
      <path d="M4 16h16" />
      <path d="M9.4 16l-.7-3.4h6.6l-.7 3.4" />
      <path d="M12 12.6v-2" />
      <path d={leafPath(12, 10.6, 1.9)} />
      <path d={leafPath(12, 10.6, 1.9, true)} />
    </>
  ),
  greenhouse: (
    <>
      <path d="M4 21V9.8L12 4.5l8 5.3V21" />
      <path d="M3 21h18" />
      <path d="M4 13h16" />
      <path d="M9 21v-8M15 21v-8" />
      <path d="M12 4.5V13" />
    </>
  ),

  // ===== Q5: How much =====
  one_pot: (
    <>
      <path d="M8 9.5h8" />
      <path d="M8.9 9.5l1.2 9h3.8l1.2-9" />
      <path d="M12 18.5v2" />
    </>
  ),
  three_pots: (
    <>
      <path d="M4 12h5" />
      <path d="M4.6 12l.8 5.4h2.2L8.4 12" />
      <path d="M9.5 12h5" />
      <path d="M10.1 12l.8 5.4h2.2l.8-5.4" />
      <path d="M15 12h5" />
      <path d="M15.6 12l.8 5.4h2.2l.8-5.4" />
      <path d="M3.5 20.6h17" />
    </>
  ),
  house_garden_rows: (
    <>
      <path d="M2.5 21h19" />
      <path d="M4 21v-6.2l4-3.3 4 3.3V21" />
      <path d="M6.8 21v-2.8h2.4V21" />
      <path d="M14.5 16.8h6.5" />
      <path d="M16 16.8v-1.8M19.2 16.8v-1.8" />
      <path d="M14.5 19.8h6.5" />
      <path d="M16 19.8V18M19.2 19.8V18" />
    </>
  ),
  barn_rows_tree: (
    <>
      <path d="M2.5 21h19" />
      <path d="M3.5 21v-5.8l3.8-3 3.8 3V21" />
      <path d="M12.8 18h4" />
      <path d="M12.8 21h4" />
      <circle cx="19.7" cy="13.6" r="2.4" />
      <path d="M19.7 16v5" />
    </>
  ),

  // ===== Q6: Why you grow =====
  harvest_basket: (
    <>
      <path d="M5 11.5h14" />
      <path d="M6 11.5l1.2 7c.2 1 1 1.7 2 1.7h5.6c1 0 1.8-.7 2-1.7l1.2-7" />
      <path d="M8.6 11.5a3.4 3.4 0 0 1 6.8 0" />
      <path d="M8.9 15h6.2" />
    </>
  ),
  flower_vase: (
    <>
      <path d="M9.6 12h4.8l1.1 9H8.5z" />
      <path d="M12 12V7.4" />
      <circle cx="12" cy="5.6" r="1.4" />
      <path d="M12 4.2V2.8M13.4 5.6h1.4M10.6 5.6H9.2M13 4.6l1-1M11 4.6l-1-1" />
    </>
  ),
  thumb_leaf: (
    <>
      <path d="M8.5 12.5l2.8-4.7.4-2.8c.1-.9 1.1-1.4 1.9-.9.5.3.8.9.7 1.5l-.6 3.4h3.5c1.1 0 2 1.1 1.7 2.2l-1.3 5.2c-.2.9-1 1.6-2 1.6H8.5" />
      <path d="M8.5 11.5H5.5V19h3z" />
      <path d={leafPath(13.6, 4.1, 1.9)} />
    </>
  ),
  leaf_still_water: (
    <>
      <path d="M12.6 13.4c-3.7-.3-5.9-2.4-6.3-6.1 3.7.3 5.9 2.4 6.3 6.1z" />
      <path d="M12.6 13.4C10.8 11 9.2 9.4 7.9 8.5" />
      <path d="M5 17.5h14" />
      <path d="M7.5 20.5h9" />
    </>
  ),
  leaf_shield: (
    <>
      <path d="M12 21c-4.5-1.7-7-4.9-7-9.5V6l7-2.9L19 6v5.5c0 4.6-2.5 7.8-7 9.5z" />
      <path d="M12.5 14.8c-2.7-.2-4.2-1.7-4.5-4.5 2.7.2 4.2 1.7 4.5 4.5z" />
    </>
  ),

  // ===== Q7: Struggles =====
  droplet_root: (
    <>
      <path d="M12 3.5c2.4 2.9 3.6 5.1 3.6 6.7a3.6 3.6 0 0 1-7.2 0c0-1.6 1.2-3.8 3.6-6.7z" />
      <path d="M12 14.5v3" />
      <path d="M12 17.5l-3 2.6M12 17.5l3 2.6M12 17.5v3.7" />
    </>
  ),
  drooping_leaf: (
    <>
      <path d="M6 20.5c.6-5.2 2.4-8.6 5.8-10.6" />
      <path d="M11.8 9.9c3.3-1.2 5.9-.8 7.7 1.2-1.5 2.3-4 3.2-7.4 2.7-.6-1.3-.7-2.6-.3-3.9z" />
    </>
  ),
  closed_bud: (
    <>
      <path d="M12 4.5c2.9 2.6 4.4 5 4.4 7.2 0 2.8-1.9 4.6-4.4 4.6s-4.4-1.8-4.4-4.6c0-2.2 1.5-4.6 4.4-7.2z" />
      <path d="M12 7.5v6" />
      <path d="M12 16.3V21" />
    </>
  ),
  wilted_pot: (
    <>
      <path d="M7.5 16.5h9" />
      <path d="M8.4 16.5l.9 4.5h5.4l.9-4.5" />
      <path d="M12 16.5v-5.3c0-2.4 1.4-3.7 3.6-3.5 1.2.1 2 .8 2.3 1.9" />
      <path d="M17.9 9.6c.5 1.9 0 3.3-1.5 4.3-1-1.4-1-2.9 0-4.5.6-.1 1.1-.1 1.5.2z" />
    </>
  ),
  beetle: (
    <>
      <ellipse cx="12" cy="13.4" rx="4" ry="5.1" />
      <circle cx="12" cy="6.2" r="1.7" />
      <path d="M12 8.3v10.2" />
      <path d="M11 5l-1.2-1.6M13 5l1.2-1.6" />
      <path d="M8 11l-2.6-1.4M8 13.8H5M8 16.2l-2.6 1.6" />
      <path d="M16 11l2.6-1.4M16 13.8h3M16 16.2l2.6 1.6" />
    </>
  ),
  smell_bag: (
    <>
      <path d="M6.8 21l1-11h8.4l1 11z" />
      <path d="M9.4 10V8.2h5.2V10" />
      <path d="M9.5 6.2c.5-.8.5-1.7 0-2.5M12 6.4c.5-.8.5-1.7 0-2.5M14.5 6.2c.5-.8.5-1.7 0-2.5" />
    </>
  ),
  no_chemicals: (
    <>
      <path d="M15 8.7c1.8 2.2 2.7 3.9 2.7 5.1a2.9 2.9 0 0 1-5.8 0c0-1.2.9-2.9 2.7-5.1z" />
      <path d="M9.1 11.2c-2.6-.2-4.1-1.7-4.4-4.4 2.6.2 4.1 1.7 4.4 4.4z" />
      <path d="M5 19.5l14-15" />
    </>
  ),
  cracked_soil: (
    <>
      <path d="M4.5 9.5h15V19h-15z" />
      <path d="M9.4 9.5l1.2 2.7-1 2.5 1.1 4.3" />
      <path d="M15.2 9.5l-1 2.3 1.2 2.6-1 4.6" />
    </>
  ),
  book_leaf: (
    <>
      <path d="M12 6.4c-1.8-1.3-4-1.9-6.5-1.9V18c2.5 0 4.7.6 6.5 1.9 1.8-1.3 4-1.9 6.5-1.9V4.5c-2.5 0-4.7.6-6.5 1.9z" />
      <path d="M12 6.4v13.5" />
      <path d={leafPath(16.2, 11.6, 1.8)} />
    </>
  ),
  grass_bare_ring: (
    <>
      <path d="M4 20.5h16" />
      <path d="M5.8 20.5v-3.2M7.8 20.5v-4.6" />
      <path d="M16.2 20.5v-4.6M18.2 20.5v-3.2" />
      <path d="M9.5 20.5a2.5 1.4 0 0 1 5 0" />
    </>
  ),

  // ===== Q9: What it will mean =====
  mountain_flag: (
    <>
      <path d="M3 20.5l6.5-12 3.8 6.8 2.4-3.8L21 20.5z" />
      <path d="M9.5 8.5V3.2" />
      <path d="M9.5 3.2h3.8v2.6H9.5" />
    </>
  ),
  house_vine: (
    <>
      <path d="M4 21v-8.5L12 6l8 6.5V21" />
      <path d="M3 21h18" />
      <path d="M10.5 21v-4.4h3V21" />
      <path d="M6.4 19c0-3.6 1-6.4 3.2-8.4" />
      <path d={leafPath(7.2, 15.2, 1.6)} />
      <path d={leafPath(8.6, 12, 1.6, true)} />
    </>
  ),
  basket_out: (
    <>
      <path d="M6.5 9.5h11" />
      <path d="M7.3 9.5l1 5.4c.2 1 1 1.6 2 1.6h3.4c1 0 1.8-.6 2-1.6l1-5.4" />
      <path d="M9.3 9.5a2.7 2.7 0 0 1 5.4 0" />
      <path d="M4.5 20.5c2.4 1 5 1.1 7.5.4s4.6-2 6.5-3.9" />
    </>
  ),
  hand_sprout: (
    <>
      <path d="M4.5 17c2.4 1.9 5.1 2.5 7.9 1.8 2.3-.6 4.4-2 6.1-3.9" />
      <path d="M4.5 17l-1.3 2.2" />
      <path d="M12 15.4v-3.2" />
      <path d={leafPath(12, 12.2, 2.2)} />
      <path d={leafPath(12, 12.2, 2.2, true)} />
    </>
  ),
  plate_leaf: (
    <>
      <path d="M4.5 15.5h15" />
      <path d="M6.5 15.5c.4 2.1 2.8 3.4 5.5 3.4s5.1-1.3 5.5-3.4" />
      <path d="M12.6 12.6c-3-.2-4.8-1.9-5.1-5 3-.2 4.8 1.9 5.1 5z" />
    </>
  ),
};

/**
 * Q4 lawn size: one grass patch icon that grows wider per option,
 * small to large, left to right. size 1 (small) to 5 (large).
 */
const GrassPatch: React.FC<{ size: number } & SvgProps> = ({ size, className }) => {
  const clamped = Math.max(1, Math.min(5, size));
  const halfWidth = 2.4 + clamped * 1.7;
  const bladeCount = clamped + 2;
  const blades: React.ReactNode[] = [];
  for (let i = 0; i < bladeCount; i++) {
    const x = 12 - halfWidth + (i * (halfWidth * 2)) / (bladeCount - 1);
    const h = i % 2 === 0 ? 4.6 : 6.4;
    const bend = i % 2 === 0 ? -1 : 1;
    blades.push(<path key={i} d={`M${x} 20c${bend * 0.5} -${h * 0.45} ${bend * 0.4} -${h * 0.8} ${bend * -0.2} -${h}`} />);
  }
  return (
    <Svg className={className}>
      <path d={`M${12 - halfWidth - 1} 20h${halfWidth * 2 + 2}`} />
      {blades}
    </Svg>
  );
};

/**
 * Q8 experience: one growth icon that matures per option:
 * seed (1), sprout (2), plant (3), tree (4).
 */
const GrowthStage: React.FC<{ stage: number } & SvgProps> = ({ stage, className }) => {
  const clamped = Math.max(1, Math.min(4, stage));
  return (
    <Svg className={className}>
      <path d="M7 20.5h10" />
      {clamped === 1 && (
        <>
          <ellipse cx="12" cy="15.2" rx="3" ry="4" />
          <path d="M12 12.6v2.4" />
        </>
      )}
      {clamped === 2 && (
        <>
          <path d="M12 20.5v-6" />
          <path d={leafPath(12, 14.5, 2.4)} />
          <path d={leafPath(12, 14.5, 2.4, true)} />
        </>
      )}
      {clamped === 3 && (
        <>
          <path d="M12 20.5V9" />
          <path d={leafPath(12, 15.5, 2.2)} />
          <path d={leafPath(12, 12.5, 2.2, true)} />
          <path d={leafPath(12, 9.4, 2)} />
        </>
      )}
      {clamped === 4 && (
        <>
          <path d="M12 20.5v-7" />
          <circle cx="12" cy="8.6" r="5" />
        </>
      )}
    </Svg>
  );
};

export interface DrManiIconProps extends SvgProps {
  name: string;
}

/**
 * Renders the brand line icon for an answer option by its icon key.
 * Falls back to the seedling (the closest icon in the same style) for any
 * unlisted key, per the ICON MAP fallback rule.
 */
export const DrManiIcon: React.FC<DrManiIconProps> = ({ name, className, size }) => {
  const patchMatch = name.match(/^grass_patch_(\d)$/);
  if (patchMatch) {
    return <GrassPatch size={Number(patchMatch[1])} className={className} />;
  }
  const growthMatch = name.match(/^growth_(\d)$/);
  if (growthMatch) {
    return <GrowthStage stage={Number(growthMatch[1])} className={className} />;
  }
  const glyph = ICONS[name] || ICONS.seedling;
  return (
    <Svg className={className} size={size}>
      {glyph}
    </Svg>
  );
};

/** UX-5 counter chip mark: a small sprout. */
export const DmmSproutMark: React.FC<SvgProps> = ({ className, size = 16 }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 21v-8" />
    <path d={leafPath(12, 13, 3)} />
    <path d={leafPath(12, 13, 3, true)} />
  </svg>
);

/**
 * UX-6 build-up visual: the seed mark growing stage by stage.
 * 1 the seed. 2 the seed cracks, a root. 3 a sprout. 4 leaves.
 * 5 the small plant complete.
 */
export const DmmSeedStage: React.FC<{ stage: number } & SvgProps> = ({ stage, className, size = 72 }) => {
  const clamped = Math.max(1, Math.min(5, stage));
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4.5 16.5h15" />
      {clamped === 1 && <ellipse cx="12" cy="13.2" rx="2.4" ry="3" />}
      {clamped === 2 && (
        <>
          <ellipse cx="12" cy="13.2" rx="2.4" ry="3" />
          <path d="M12 11.2v2.6" />
          <path d="M12 16.5v2.4m0 0l-1.8 1.6m1.8-1.6l1.8 1.6" />
        </>
      )}
      {clamped >= 3 && (
        <>
          <path d="M12 16.5v2.4m0 0l-1.8 1.6m1.8-1.6l1.8 1.6" />
          <path d={`M12 16.5V${clamped === 3 ? 11 : clamped === 4 ? 8.5 : 6.5}`} />
        </>
      )}
      {clamped === 3 && (
        <>
          <path d={leafPath(12, 11.4, 2.2)} />
          <path d={leafPath(12, 11.4, 2.2, true)} />
        </>
      )}
      {clamped === 4 && (
        <>
          <path d={leafPath(12, 13.2, 2.2)} />
          <path d={leafPath(12, 13.2, 2.2, true)} />
          <path d={leafPath(12, 9.2, 2)} />
          <path d={leafPath(12, 9.2, 2, true)} />
        </>
      )}
      {clamped === 5 && (
        <>
          <path d={leafPath(12, 14, 2.2)} />
          <path d={leafPath(12, 14, 2.2, true)} />
          <path d={leafPath(12, 10.4, 2)} />
          <path d={leafPath(12, 10.4, 2, true)} />
          <path d={leafPath(12.4, 6.2, 1.8)} />
        </>
      )}
    </svg>
  );
};

/** UX-7 celebration: a single drifting leaf (positioned and animated by CSS). */
export const DmmDriftLeaf: React.FC<SvgProps> = ({ className, size = 18 }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M6 18.5C6 10.5 11 5.5 19 5c.5 8-4.5 13-12.5 13.5z" />
    <path d="M6 18.5c2.2-4.6 5.4-8 9.6-10.2" />
  </svg>
);

export default DrManiIcon;
